// @flow
export const getGradeDescriptionFromNumericGrade = (grade: number): string => {
  const mapping = new Map([
    [-1, 'Pre-Kindergarten'],
    [0, 'Kindergarten'],
    [1, '1st Grade'],
    [2, '2nd Grade'],
    [3, '3rd Grade'],
    [4, '4th Grade'],
    [5, '5th Grade'],
    [6, '6th Grade'],
    [7, '7th Grade'],
    [8, '8th Grade'],
    [9, '9th Grade'],
    [10, '10th Grade'],
    [11, '11h Grade'],
    [12, '12th Grade'],
  ]);

  const retval = mapping.get(grade);

  if (retval) return retval;
  throw new Error('Key not found');
};

export const getNumeralFromReadingLevel = (readingLevel: number): string => {
  const mapping = new Map([
    [0, '0'],
    [1, '1'],
    [2, '2'],
    [3, '3'],
    [4, '4'],
    [5, '4'],
    [6, '4'],
    [7, '4'],
    [8, '4'],
    [9, '4'],
    [10, '4'],
    [11, '4'],
    [12, '4'],
  ]);

  const retval = mapping.get(readingLevel);

  if (retval) return retval;
  throw new Error('Key not found');
};

export const getLetterGradeFromNumberGrade = (numberGrade: number): string => {
  const mapping: Record<number, string> = {
    '-1': 'pk',
    0: 'k',
    1: '1',
    2: '2',
    3: '3',
    4: '4',
    5: '5',
    6: '6',
    7: '7',
    8: '8',
    9: '9',
    10: '10',
    11: '11',
    12: '12',
  };
  return mapping[numberGrade];
};
export const getGradeDescriptionFromNumberGrade = (
  inputGrade: string | number,
) => {
  const grade = (() => {
    if (typeof inputGrade === 'string') return parseInt(inputGrade, 10);
    return inputGrade;
  })();
  const mapping = new Map([
    [-1, 'PreK'],
    [0, 'K'],
    [1, '1st'],
    [2, '2nd'],
    [3, '3rd'],
    [4, '4th'],
    [5, '5th'],
    [6, '6th'],
    [7, '7th'],
    [8, '8th'],
    [9, '9th'],
    [10, '10th'],
    [11, '11th'],
    [12, '12th'],
  ]);
  return mapping.get(grade);
};
export const getNumericGradeFromReadingLevel = (readingLevel: string) => {
  const rl = parseInt(readingLevel, 10);

  if (rl > 7 || rl < 1) {
    throw new Error(
      'getNumericGradeFromReadingLevel(): readingLevel out of range',
    );
  }

  return rl - 2;
};
export const getGradeCodeFromReadingLevel = (readingLevel: string) => {
  const rl = parseInt(readingLevel, 10);

  if (rl > 7 || rl < 1) {
    throw new Error(
      'getGradeCodeFromReadingLevel(): readingLevel out of range',
    );
  }

  const mapping = ['pk', 'k', '1', '2', '3', '4', '5'];
  return mapping[rl - 1];
};
export const getRecommendedReadingLevelFromGrade = (
  grade: number,
  language: string,
) => {
  let mapping: Record<number, number>;
  if (language === 'es') {
    mapping = {
      '-1': 0,
      0: 1,
      1: 1,
      2: 1,
      3: 1,
      4: 1,
      5: 1,
      6: 1,
      7: 1,
      8: 1,
      9: 1,
      10: 1,
      11: 1,
      12: 1,
    };
  } else if (language === 'en') {
    mapping = {
      '-1': 0,
      0: 1,
      1: 2,
      2: 2,
      3: 3,
      4: 4,
      5: 4,
      6: 4,
      7: 4,
      8: 4,
      9: 4,
      10: 4,
      11: 4,
      12: 4,
    };
  } else if (language === 'ar') {
    mapping = {
      '-1': 0,
      0: 0,
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
      6: 0,
      7: 0,
      8: 0,
      9: 0,
      10: 0,
      11: 0,
      12: 0,
    };
  } else {
    throw new Error("Language is not 'en', 'es' or 'ar'");
  }
  return mapping[grade];
};

// Reading levels run 0..4, so level 0 is a real choice and a truthiness test
// silently discards it. These two helpers are the single definition of "is a
// reading level set" and "what number is stored here", shared by the teacher UI
// that writes the value, the services that persist it, and myPeekaville's
// save-file assembly that reads it back (PE-1263).
export const isReadingLevelSet = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== '';

export const parseReadingLevel = (value: unknown): number | undefined => {
  if (!isReadingLevelSet(value)) return undefined;
  if (typeof value === 'number')
    return Number.isInteger(value) ? value : undefined;

  if (typeof value === 'string' || value instanceof String) {
    // Deliberately strict: a stored display label such as
    // 'Fountas and Pinnell K - L' is corrupt data, not the level 'K', and must
    // not be coerced into a plausible-looking number.
    const asString = value.toString().trim();
    if (!/^-?\d+$/.test(asString)) return undefined;
    return parseInt(asString, 10);
  }

  return undefined;
};
